import React from 'react';
import {
  arrayBuilderItemFirstPageTitleUI,
  selectUI,
  textUI,
} from 'platform/forms-system/src/js/web-component-patterns';

import { createAllowedCharValidator } from '../../../../all-claims/validations';
import {
  MILITARY_CITIES,
  MILITARY_STATE_VALUES,
  USA,
} from '../../../constants';
import { vaTreatmentFacilityItemSchema } from '../../../schema';
import { getVaFacilityItem, STATE_LABELS } from './options';

const { treatmentCenterName, treatmentCenterAddress } =
  vaTreatmentFacilityItemSchema.properties;

// New items are created with the schema default of USA; a missing country is
// also treated as USA so state rules still apply to items lacking the default.
const isUSA = address => (address?.country || USA) === USA;

const normalize = value => (value || '').trim().toUpperCase();

/**
 * For USA addresses, a military city (APO/DPO/FPO) and a military state
 * (AA/AE/AP) must be used together.
 *
 * Attached to the address object (not the city/state fields) because it
 * compares both fields and array-builder item pages validate only the item's
 * own data.
 *
 * @param {object} errors - errors object for the address
 * @param {object} [address] - { country, state, city }
 */
export const validateMilitaryAddress = (errors, address = {}) => {
  if (!isUSA(address)) return;
  const isMilitaryCity = MILITARY_CITIES.includes(normalize(address.city));
  const isMilitaryState = MILITARY_STATE_VALUES.includes(
    normalize(address.state),
  );
  if (address.state && isMilitaryState && address.city && !isMilitaryCity) {
    errors.city.addError(
      'City must match APO, DPO, or FPO when using a military state code',
    );
  }
  if (address.city && isMilitaryCity && address.state && !isMilitaryState) {
    errors.state.addError(
      'State must be AA, AE, or AP when using a military city',
    );
  }
};

export const FacilityDetailsDescription = (
  <>
    <p>
      Tell us where you received treatment for your condition. We’ll use this
      information to locate your records.
    </p>
    <p>You can add more facilities later.</p>
  </>
);

export default {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: 'VA or military health facility details',
      nounSingular: 'facility',
      lowerCase: false,
      description: FacilityDetailsDescription,
    }),
    treatmentCenterName: textUI({
      title: 'Name of VA or military medical facility',
      errorMessages: {
        required: 'Enter the name of the facility',
      },
      validations: [
        createAllowedCharValidator(
          treatmentCenterName.pattern,
          'facility name',
        ),
      ],
    }),
    treatmentCenterAddress: {
      'ui:validations': [validateMilitaryAddress],
      country: selectUI({
        title: 'Country',
        errorMessages: { required: 'Select a country' },
      }),
      // State applies only to USA addresses.
      state: selectUI({
        title: 'State',
        labels: STATE_LABELS,
        required: (formData, index, fullData) =>
          isUSA(
            getVaFacilityItem(formData, index, fullData).treatmentCenterAddress,
          ),
        hideIf: (formData, index, fullData) =>
          !isUSA(
            getVaFacilityItem(formData, index, fullData).treatmentCenterAddress,
          ),
        errorMessages: { required: 'Select a state' },
      }),
      city: textUI({
        title: 'City',
        errorMessages: { required: 'Enter a city' },
        validations: [
          createAllowedCharValidator(
            treatmentCenterAddress.properties.city.pattern,
            'city',
          ),
        ],
      }),
    },
  },
  schema: {
    type: 'object',
    required: ['treatmentCenterName', 'treatmentCenterAddress'],
    properties: {
      treatmentCenterName,
      treatmentCenterAddress,
    },
  },
};
